import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  addCoupon,
  deleteCoupon,
  getAllCoupons,
  updateCoupon,
} from "../../../services/AdminService";
import { toast } from "react-toastify";

export default function CouponTable() {
  const apiInstance = useAxiosInstance();
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({
    couponId: null,
    code: "",
    type: "",
    amount: "",
    expirationDate: "",
    discountType: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const totalPages = Math.ceil(coupons.length / itemsPerPage);
  const paginatedCoupons = coupons.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    const fetchAllCoupons = async () => {
      setLoading(true);
      try {
        const data = await getAllCoupons(apiInstance);
        if (Array.isArray(data)) {
          setCoupons(data);
        }
      } catch (error) {
        setError(error.message || "Error getting pricing options.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllCoupons();
  }, [apiInstance]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const today = new Date().toISOString().split("T")[0];
    if (form.expirationDate && form.expirationDate < today) {
      toast.error("Expiration date cannot be in the past.");
      return;
    }

    const parsedAmount = parseFloat(form.amount);

    if (form.amount === "" || Number.isNaN(parsedAmount)) {
      toast.error("Please enter a valid amount.");
      return;
    }

    if (parsedAmount <= 0) {
      toast.error("Amount must be greater than 0.");
      return;
    }

    if (form.discountType === "Percent Off") {
      if (parsedAmount > 100) {
        toast.error("Percentage cannot exceed 100%.");
        return;
      }
    }
    if (editing) {
      setCoupons(coupons.map((c) => (c.couponId === form.couponId ? form : c)));
      try {
        await updateCoupon(apiInstance, form.couponId, {
          code: form.code,
          type: form.type,
          discountType: form.discountType,
          amount: parsedAmount,
          expirationDate:
            form.expirationDate === "" ? "0001-01-01" : form.expirationDate,
          usageCount: 0,
        });

        try {
          const data = await getAllCoupons(apiInstance);
          if (Array.isArray(data)) {
            setCoupons(data);
          }
        } catch (error) {
          setError(error.message || "Error getting pricing options.");
        } finally {
          setLoading(false);
        }

        toast.success("Coupon updated successfully!");
      } catch (error) {
        setError("Failed to updated coupon.");
        toast.error("Failed to updated coupon.");
      }
    } else {
      setCoupons([...coupons, { ...form, couponId: Date.now() }]);
      try {
        await addCoupon(apiInstance, {
          code: form.code,
          type: form.type,
          discountType: form.discountType,
          amount: parsedAmount,
          expirationDate:
            form.expirationDate === "" ? "0001-01-01" : form.expirationDate,
          usageCount: 0,
        });
        try {
          const data = await getAllCoupons(apiInstance);
          if (Array.isArray(data)) {
            setCoupons(data);
          }
        } catch (error) {
          setError(error.message || "Error getting pricing options.");
        } finally {
          setLoading(false);
        }
        toast.success("Coupon added successfully!");
      } catch (error) {
        setError("Failed to add coupon.");
        toast.error("Failed to add coupon.");
      }
    }
    setForm({
      couponId: null,
      code: "",
      type: "",
      amount: "",
      expirationDate: "",
      discountType: "",
    });
    setShowForm(false);
    setEditing(false);
  };

  const handleEdit = (coupon) => {
    setForm({
      ...coupon,
      discountType: coupon.discountType ?? "",
      expirationDate: coupon.expirationDate
        ? coupon.expirationDate.split("T")[0]
        : "",
    });

    setEditing(true);
    setShowForm(true);
  };

  const handleDelete = async () => {
    setCoupons(coupons.filter((c) => c.couponId !== confirmDeleteId));
    setConfirmDeleteId(null);
    try {
      await deleteCoupon(apiInstance, confirmDeleteId);

      toast.success("Coupon deleted successfully!");
    } catch (error) {
      setError("Failed to delete coupon.");
      toast.error("Failed to delete coupon.");
    }
  };

  const utcToLocalDate = (utcDateString) => {
    const date = new Date(utcDateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="hidden">Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="w-full bg-white shadow-lg rounded-lg py-4 border-gray-300 px-4 mt-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h2 className="text-xl  uppercase">Coupons</h2>
        <button
          className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-800 text-white uppercase text-sm"
          onClick={() => {
            setShowForm(true);
            setForm({
              couponId: null,
              code: "",
              type: "",
              amount: "",
              expirationDate: "",
              discountType: "",
            });
            setEditing(false);
          }}
        >
          + New Coupon
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2 text-left">Code</th>
              <th className="border p-2 text-left">Type</th>
              <th className="border p-2 text-left">Discount Type</th>
              <th className="border p-2 text-left">Amount</th>
              <th className="border p-2 text-left">Expiration</th>
              <th className="border p-2 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedCoupons.map((coupon) => (
              <tr key={coupon.couponId}>
                <td className="border p-2 whitespace-nowrap">{coupon.code}</td>
                <td className="border p-2 whitespace-nowrap">{coupon.type}</td>
                <td className="border p-2 whitespace-nowrap">
                  {coupon.discountType}
                </td>
                <td className="border p-2 whitespace-nowrap">
                  {coupon.amount ? Number(coupon.amount).toFixed(2) : "0.00"}
                </td>
                <td className="border p-2 whitespace-nowrap">
                  {coupon.expirationDate === "0001-01-01T00:00:00"
                    ? "Never"
                    : utcToLocalDate(coupon.expirationDate)}
                </td>
                <td className="border p-2 space-x-2 whitespace-nowrap">
                  <button
                    className="px-3 py-1  text-blue-700"
                    onClick={() => handleEdit(coupon)}
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                  </button>
                  <button
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700"
                    onClick={() => setConfirmDeleteId(coupon.couponId)}
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-end items-center mt-4 space-y-2 sm:space-y-0 sm:space-x-2">
        <button
          className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Prev
        </button>
        <span className="px-2 text-sm">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {/* Form and Delete modals remain unchanged */}
      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editing ? "Edit Coupon" : "Add Coupon"}
            </h3>
            <form onSubmit={handleFormSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  className="w-full border p-2 rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, type: e.target.value }))
                  }
                  className="w-full border p-2 rounded"
                  required
                >
                  <option value="">Select type</option>
                  <option value="Combine">Combine</option>
                  <option value="CombineAndPositionDrills">
                    Combine And PositionDrills
                  </option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Discount Type
                </label>
                <select
                  value={form.discountType || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      discountType: e.target.value,
                    }))
                  }
                  className="w-full border p-2 rounded"
                  required
                >
                  <option value="">Select discount type</option>
                  <option value="Amount Off">Amount Off</option>
                  <option value="Percent Off">Percent Off</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  className="w-full border p-2 rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Expiration
                </label>
                <input
                  type="date"
                  value={form.expirationDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      expirationDate: e.target.value,
                    }))
                  }
                  className="w-full border p-2 rounded"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border rounded"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-500 text-white rounded"
                >
                  {editing ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p>Are you sure you want to delete this coupon?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
